import { useState, useCallback, useEffect } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";

export const usePlanLimits = () => {
  const [planStatus, setPlanStatus] = useState(null);
  const [limitContext, setLimitContext] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [topUpModalMode, setTopUpModalMode] = useState('topup');
  const [topUpReason, setTopUpReason] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const fetchPlanStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth("/api/subscription/status");
      if (response.ok) {
        const data = await response.json();
        setPlanStatus(data);
      }
    } catch (error) {
      console.error("Error fetching plan status:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlanStatus();
  }, [fetchPlanStatus]);

  const checkLimit = useCallback(async (limitType, planId = null) => {
    try {
      const response = await fetchWithAuth("/api/subscription/check-limit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limitType, planId }),
      });
      
      if (!response.ok) {
        console.error("Error checking limit");
        return true;
      }
      
      const data = await response.json();
      
      if (!data.allowed) {
        if (data.limitReached) {
          setTopUpModalMode('upgrade');
          setTopUpReason('runLimit');
          setIsTopUpModalOpen(true);
          return false;
        }
        if (data.insufficientCredits) {
          setTopUpModalMode('topup');
          setTopUpReason('credits');
          setIsTopUpModalOpen(true);
          return false;
        }
        navigate(`/upgrade?context=${data.limitType || "default"}`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error("Error checking limit:", error);
      return true;
    }
  }, [navigate]);

  /**
   * Check if the user has enough credits for a given action.
   * Returns true if they do, false + opens top-up modal if not.
   */
  const checkCredits = useCallback(async (actionType) => {
    try {
      const response = await fetchWithAuth(`/api/credits/check?action=${encodeURIComponent(actionType)}`);
      if (!response.ok) return true; // fail-open

      const data = await response.json();
      if (!data.allowed) {
        setIsTopUpModalOpen(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking credits:", error);
      return true; // fail-open
    }
  }, []);

  /**
   * Deduct credits after a successful action completes.
   */
  const deductCredits = useCallback(async (actionType) => {
    try {
      const response = await fetchWithAuth("/api/credits/deduct", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: actionType }),
      });
      if (response.ok) {
        const data = await response.json();
        // Refresh plan status so credit balance updates
        await fetchPlanStatus();
        return data;
      }
    } catch (error) {
      console.error("Error deducting credits:", error);
    }
    return { success: false };
  }, [fetchPlanStatus]);

  const incrementUsage = useCallback(async (usageType, planId = null) => {
    try {
      await fetchWithAuth("/api/subscription/increment-usage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ usageType, planId }),
      });
      await fetchPlanStatus();
    } catch (error) {
      console.error("Error incrementing usage:", error);
    }
  }, [fetchPlanStatus]);

  const openUpgradeModal = useCallback((context = null) => {
    setLimitContext(context);
    navigate(`/upgrade?context=${context?.limitType || "default"}`);
  }, [navigate]);

  const openTopUpModal = useCallback((mode = 'topup', reason = null) => {
    setTopUpModalMode(mode);
    setTopUpReason(reason);
    setIsTopUpModalOpen(true);
  }, []);

  const closeTopUpModal = useCallback(() => {
    setIsTopUpModalOpen(false);
    setTopUpModalMode('topup');
    setTopUpReason(null);
    fetchPlanStatus();
  }, [fetchPlanStatus]);

  const closeUpgradeModal = useCallback(() => {
    setLimitContext(null);
    onClose();
  }, [onClose]);

  const withLimitCheck = useCallback(
    async (limitType, action, planId = null) => {
      const allowed = await checkLimit(limitType, planId);
      if (allowed) {
        try {
          const result = await action();
          if (result !== null && result !== undefined) {
            await incrementUsage(limitType, planId);
          }
          return result;
        } catch (error) {
          console.error("Action failed, usage not incremented:", error);
          throw error;
        }
      }
      return null;
    },
    [checkLimit, incrementUsage]
  );

  const isPro = planStatus?.planType === "pro";
  const creditBalance = planStatus?.credits?.total ?? 0;
  const creditAllowance = planStatus?.credits?.allowance ?? (isPro ? 300 : 30);
  const hasSavedPaymentMethod = planStatus?.hasSavedPaymentMethod ?? false;
  const creditsResetAt = planStatus?.credits?.resetAt ?? null;

  const hasEnoughCredits = useCallback((actionType) => {
    if (!planStatus?.credits) return true; // optimistic until loaded
    const fallbackCosts = { 'pulse': 1, 'strategy-section': 2, 'action-plan': 3, 'document': 4, 'competitor-analysis': 8 };
    const backendCosts = planStatus.credits.costs || {};
    const entry = backendCosts[actionType];
    // entry is now { credits, isGated } — fall back to plain number for older responses
    const cost   = entry?.credits  ?? entry ?? fallbackCosts[actionType] ?? 0;
    const gated  = entry?.isGated  ?? true;
    if (!gated) return true; // not credit-gated — always allow
    return creditBalance >= cost;
  }, [planStatus, creditBalance]);

  const getFeatureGateState = useCallback((limitType, planId = null) => {
    const config = {
      strategy: {
        creditAction: 'strategy-section',
        maxKey: 'maxStrategyRegens',
        countKey: 'strategyCount',
        label: 'strategy regeneration',
        creditLabel: '2 credits',
      },
      competitor: {
        creditAction: 'competitor-analysis',
        maxKey: 'maxCompetitorRegens',
        countKey: 'competitorCount',
        label: 'competitor analysis',
        creditLabel: '8 credits',
      },
      docs: {
        creditAction: 'document',
        maxKey: 'maxDocsGenerations',
        countKey: 'docsCount',
        label: 'document generation',
        creditLabel: '4 credits',
      },
      action_plan: {
        creditAction: 'action-plan',
        maxKey: 'maxActionPlanGenerations',
        countKey: 'actionPlanCount',
        label: 'action plan generation',
        creditLabel: '3 credits',
      },
    }[limitType];

    if (!config) {
      return { allowed: true, insufficientCredits: false, limitReached: false, tooltip: undefined };
    }

    const enoughCredits = hasEnoughCredits(config.creditAction);
    const planUsage = planId ? planStatus?.usage?.planUsage?.[String(planId)] : null;
    const maxCount = planStatus?.limits?.[config.maxKey];
    const currentCount = planUsage?.[config.countKey] ?? 0;
    const limitReached = maxCount !== null && maxCount !== undefined && currentCount >= maxCount;

    let tooltip;
    if (limitReached) {
      tooltip = `You've used ${currentCount} of ${maxCount} ${config.label} run${maxCount === 1 ? '' : 's'} for this project. Upgrade to run it again.`;
    } else if (!enoughCredits) {
      tooltip = `At least ${config.creditLabel} are needed for this feature — check your balance in Usage & top up to continue.`;
    }

    return {
      allowed: enoughCredits && !limitReached,
      insufficientCredits: !enoughCredits,
      limitReached,
      currentCount,
      maxCount,
      tooltip,
    };
  }, [hasEnoughCredits, planStatus]);

  return {
    planStatus,
    isPro,
    isLoading,
    isUpgradeModalOpen: isOpen,
    isTopUpModalOpen,
    limitContext,
    creditBalance,
    creditAllowance,
    hasSavedPaymentMethod,
    creditsResetAt,
    hasEnoughCredits,
    getFeatureGateState,
    checkLimit,
    checkCredits,
    deductCredits,
    incrementUsage,
    openUpgradeModal,
    topUpModalMode,
    topUpReason,
    openTopUpModal,
    closeTopUpModal,
    closeUpgradeModal,
    refreshPlanStatus: fetchPlanStatus,
    withLimitCheck,
  };
};

export default usePlanLimits;
